import React from 'react'
import { Table, Card } from 'antd'
import styled from 'styled-components'
import { useMediaQuery } from 'react-responsive'

// 莫兰迪色系配色
const colors = {
  cardBackground: '#faf9f6',
  border: 'rgba(168, 181, 192, 0.3)',
  shadow: 'rgba(154, 163, 175, 0.2)',
  textPrimary: '#5a5a5a',
  textSecondary: '#8a8a8a'
}

const TableContainer = styled.div`
  width: 100%;
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;

  @media (max-width: 768px) {
    overflow-x: visible;
  }
`

const CardList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;

  @media (max-width: 768px) {
    gap: 10px;
  }
`

const CardItem = styled(Card)`
  background: ${colors.cardBackground};
  border-radius: 12px;
  box-shadow: 0 2px 8px ${colors.shadow};
  border: 1px solid ${colors.border};
  margin-bottom: 0;

  .ant-card-body {
    padding: 16px;
  }
`

const CardRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 8px 0;
  border-bottom: 1px solid ${colors.border};

  &:last-child {
    border-bottom: none;
  }
`

const CardLabel = styled.span`
  font-size: 12px;
  color: ${colors.textSecondary};
  font-weight: 500;
  margin-bottom: 4px;
  display: block;
`

const CardValue = styled.span`
  font-size: 14px;
  color: ${colors.textPrimary};
  word-break: break-word;
`

const CardActions = styled.div`
  display: flex;
  gap: 8px;
  margin-top: 12px;
  flex-wrap: wrap;
`

const PaginationWrapper = styled.div`
  margin-top: 16px;
  display: flex;
  justify-content: center;

  @media (max-width: 768px) {
    .ant-pagination {
      font-size: 12px;
    }

    .ant-pagination-item,
    .ant-pagination-prev,
    .ant-pagination-next {
      min-width: 28px;
      height: 28px;
      line-height: 28px;
    }
  }
`

const AdminTable = ({
  columns,
  dataSource,
  loading,
  pagination,
  onChange,
  scroll = { x: true },
  rowSelection,
  rowKey = '_id'
}) => {
  const isMobile = useMediaQuery({ maxWidth: 768 })

  const paginationConfig = {
    current: pagination.currentPage || pagination.current,
    total: pagination.totalItems || pagination.total,
    pageSize: pagination.itemsPerPage || pagination.pageSize,
    showSizeChanger: !isMobile,
    showTotal: (total) => `共 ${total} 條記錄`,
    pageSizeOptions: ['10', '20', '50', '100'],
    responsive: true
  }

  // 手机版：卡片布局
  if (isMobile) {
    return (
      <div>
        <CardList>
          {loading ? (
            <CardItem>
              <div style={{ textAlign: 'center', padding: '20px' }}>
                載入中...
              </div>
            </CardItem>
          ) : dataSource && dataSource.length > 0 ? (
            dataSource.map((record, index) => {
              const recordKey = record[rowKey] || record.id || index
              const isSelected =
                rowSelection &&
                rowSelection.selectedRowKeys?.includes(recordKey)

              return (
                <CardItem key={recordKey}>
                  {rowSelection && (
                    <div style={{ marginBottom: '12px' }}>
                      <input
                        type='checkbox'
                        checked={isSelected}
                        onChange={(e) => {
                          if (rowSelection.onChange) {
                            const currentKeys =
                              rowSelection.selectedRowKeys || []
                            const newKeys = e.target.checked
                              ? [...currentKeys, recordKey]
                              : currentKeys.filter((key) => key !== recordKey)
                            rowSelection.onChange(newKeys)
                          }
                        }}
                        style={{ marginRight: '8px' }}
                      />
                      <span
                        style={{ fontSize: '14px', color: colors.textPrimary }}
                      >
                        選擇
                      </span>
                    </div>
                  )}
                  {columns.map((column) => {
                    if (column.key === 'action' || column.key === '操作') {
                      return (
                        <CardActions key={column.key || column.dataIndex}>
                          {column.render
                            ? column.render(
                                record[column.dataIndex],
                                record,
                                index
                              )
                            : null}
                        </CardActions>
                      )
                    }

                    const value = record[column.dataIndex]
                    const displayValue = column.render
                      ? column.render(value, record, index)
                      : value

                    if (displayValue === null || displayValue === undefined) {
                      return null
                    }

                    return (
                      <CardRow key={column.key || column.dataIndex}>
                        <CardLabel>{column.title}</CardLabel>
                        <CardValue>{displayValue}</CardValue>
                      </CardRow>
                    )
                  })}
                </CardItem>
              )
            })
          ) : (
            <CardItem>
              <div
                style={{
                  textAlign: 'center',
                  padding: '20px',
                  color: colors.textSecondary
                }}
              >
                暫無數據
              </div>
            </CardItem>
          )}
        </CardList>
        {paginationConfig.total > 0 && (
          <PaginationWrapper>
            <div
              style={{
                display: 'flex',
                justifyContent: 'center',
                width: '100%'
              }}
            >
              {/* 使用简单的分页控件 */}
              <div
                style={{
                  display: 'flex',
                  gap: '8px',
                  alignItems: 'center',
                  flexWrap: 'wrap',
                  justifyContent: 'center'
                }}
              >
                <button
                  onClick={() => {
                    if (paginationConfig.current > 1 && onChange) {
                      onChange({
                        current: paginationConfig.current - 1,
                        pageSize: paginationConfig.pageSize
                      })
                    }
                  }}
                  disabled={paginationConfig.current === 1}
                  style={{
                    padding: '6px 12px',
                    border: `1px solid ${colors.border}`,
                    borderRadius: '8px',
                    background: colors.cardBackground,
                    color: colors.textPrimary,
                    cursor:
                      paginationConfig.current === 1
                        ? 'not-allowed'
                        : 'pointer',
                    opacity: paginationConfig.current === 1 ? 0.5 : 1
                  }}
                >
                  上一頁
                </button>
                <span style={{ color: colors.textPrimary, fontSize: '14px' }}>
                  第 {paginationConfig.current} /{' '}
                  {Math.ceil(
                    paginationConfig.total / paginationConfig.pageSize
                  )}{' '}
                  頁
                </span>
                <button
                  onClick={() => {
                    const totalPages = Math.ceil(
                      paginationConfig.total / paginationConfig.pageSize
                    )
                    if (paginationConfig.current < totalPages && onChange) {
                      onChange({
                        current: paginationConfig.current + 1,
                        pageSize: paginationConfig.pageSize
                      })
                    }
                  }}
                  disabled={
                    paginationConfig.current >=
                    Math.ceil(
                      paginationConfig.total / paginationConfig.pageSize
                    )
                  }
                  style={{
                    padding: '6px 12px',
                    border: `1px solid ${colors.border}`,
                    borderRadius: '8px',
                    background: colors.cardBackground,
                    color: colors.textPrimary,
                    cursor:
                      paginationConfig.current >=
                      Math.ceil(
                        paginationConfig.total / paginationConfig.pageSize
                      )
                        ? 'not-allowed'
                        : 'pointer',
                    opacity:
                      paginationConfig.current >=
                      Math.ceil(
                        paginationConfig.total / paginationConfig.pageSize
                      )
                        ? 0.5
                        : 1
                  }}
                >
                  下一頁
                </button>
              </div>
            </div>
            <div
              style={{
                textAlign: 'center',
                marginTop: '8px',
                color: colors.textSecondary,
                fontSize: '12px'
              }}
            >
              {paginationConfig.showTotal(paginationConfig.total)}
            </div>
          </PaginationWrapper>
        )}
      </div>
    )
  }

  // 桌面版：表格布局
  return (
    <TableContainer>
      <Table
        columns={columns}
        dataSource={dataSource}
        loading={loading}
        pagination={paginationConfig}
        onChange={onChange}
        scroll={scroll}
        rowKey={rowKey}
        rowSelection={rowSelection}
        style={{ width: '100%' }}
      />
    </TableContainer>
  )
}

export default AdminTable
